import {
  Button,
  Form,
  Input,
  message,
  Upload,
  UploadFile,
  DatePicker,
  Flex,
  Modal,
  Space,
  Divider,
  Row,
  Col,
  Select,
  Cascader,
  Spin,
  InputNumber,
  Radio
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DeleteOutlined,
  LeftOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useEffect, createRef, useRef } from 'react';
import { UploadChangeParam } from 'antd/es/upload';
import {
  createExhibition,
  ExhibitionContent,
  ExhibitionStatus,
  ExhibitionUpdateData,
  getExhibitionDetail,
  updateExhibition,
} from '@/api/exhibition';
import dayjs from 'dayjs';
import RichEditor, { RichEditorRef } from '@/components/RichEditor';
import { Delta, Op } from 'quill';
import { uploadImage } from '@/api/common';
import { Semaphore } from '@/utils/semaphore';
import { EventRenderer } from '@supp-kit/events';
import UploadImage from './upload';
import Places from './map';
import { getOptions, getFeatures } from './utils';
import locationIcon from '@/assets/location.svg';
import "../../../node_modules/@supp-kit/events/dist/index.css"
import "./editor.less"

const onlyShowRoad = true;
const locationOptions = getOptions().map((item) => {
  if (onlyShowRoad) {
    return { label: item.label, value: item.value };
  }

  return item;
});
const locationFeatures = getFeatures();

const { RangePicker } = DatePicker;

interface ExhibitionImageInfo extends UploadFile {
  ratio: number;
}

interface ExtraContent {
  type: 'image' | 'text';
  imageInfo: ExhibitionImageInfo | null;
  text: Delta;
  editorRef: React.RefObject<RichEditorRef>;
}

interface IForm {
  cover: UploadFile[];
  title: string;
  description: string;
  venue: string;
  schedule: [dayjs.Dayjs, dayjs.Dayjs];
  hours: string;
  close: string;
  fee: string;
  website: string;
  location: string;
  access: string;
  phone: string;
  postCode: string;
  venuePhotos: UploadFile[];
}

enum TypeEnum {
  ArtMuseumExhibition = 1,
  GalleryExhibition,
  ThemeExhibition,
  Digital,
  Shop
}

const TypeMap = new Map([
  [TypeEnum.ArtMuseumExhibition, '美术馆展'],
  [TypeEnum.GalleryExhibition, '画廊展'],
  [TypeEnum.ThemeExhibition, '主题展/快闪展'],
  [TypeEnum.Digital, '数字/新媒体艺术'],
  [TypeEnum.Shop, '商店'],
]);

interface ILocation {
  address?: string;
  lat?: string | number;
  lng?: string | number;
}

enum RewardTypeEnum {
  // Coins = 1,
  Givs = 1
}

const RewardTypeEnumMap = new Map([
  // [RewardTypeEnum.Coins, '金币'],
  [RewardTypeEnum.Givs, '活动金币'],
]);

enum IconTypeEnum {
  Default,
  Custom
}

const IconTypeEnumMap = new Map([
  [IconTypeEnum.Default, '默认图标'],
  [IconTypeEnum.Custom, '自定义图标'],
]);

const iconUrlDefaultArr = [
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (33).png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (8).png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (4).png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (5).png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame 1984079216.png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (6).png",
  "https://prodsuppart-s3.suppart.app/artworkCover/Frame (7).png"
];

const ExhibitionEditor = () => {
  const navigate = useNavigate();
  const [modal, modalContextHolder] = Modal.useModal();

  const [form] = Form.useForm<IForm>();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [fileList, setFileList] = useState<ExhibitionImageInfo[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [extraContent, setExtraContent] = useState<ExtraContent[]>([]);
  /**
   * 保存する ext_data を作る。
   * factpack(現地情報・封面クレジット)がある行では**それを壊さない**。
   * 追加コンテンツは content キーに入れる。
   */
  const mergeExtData = (content: ExtraContent[]) => {
    const fp = factpackRef.current;
    if (!fp) return content;
    return content && content.length ? { ...fp, content } : fp;
  };
  // 🔴 ext_data は 2 つの用途で共用されている(2026-09-06)。
  //   ①この編集画面の「追加コンテンツ」= 配列 [{imageUrl,deltaContent,...}]
  //   ②催事頁の factpack / 封面クレジット = オブジェクト {"v":1,"facts":[...],"credit":"..."}
  //     (投入側 ~/exhibition_import/evkit.py の set_facts と、
  //      表示側 supp_mobile の src/utils/eventFacts.js / ops/seo-edge/seo-inject.js)
  //   ②の行を開くと JSON.parse の結果がオブジェクトなので .map() で落ち、
  //   **編集画面が何も表示されなくなっていた**(実測 id=4498)。
  //   さらに保存時に extData を extraContent で丸ごと上書きするので、
  //   開いて保存するだけで factpack と署名が消える。
  //   → ②はそのまま持ち回し、保存時に書き戻す。
  const factpackRef = useRef<Record<string, unknown> | null>(null);
  const [previewData, setPreviewData] = useState<any>({});

  const formValues = form.getFieldsValue();

  // useEffect(() => {
  //   console.log(formValues, 'formValues');
  // }, [formValues]);

  const transformFormToPreview = (values: IForm) => {
    if (!values) return {};

    // const getImageInfo = () => {
    //   if (!fileList.length) return [];

    //   const file = fileList[0];
    //   if (file.url) {
    //     // Existing image
    //     return [
    //       {
    //         imageUrl: file.url,
    //         ratio: file.ratio || 0,
    //         type: 1,
    //         sort: 0,
    //       },
    //     ];
    //   } else if (file.originFileObj) {
    //     // New uploaded image
    //     return [
    //       {
    //         imageUrl: URL.createObjectURL(file.originFileObj),
    //         ratio: file.ratio || 0,
    //         type: 1,
    //         sort: 0,
    //       },
    //     ];
    //   }
    //   return [];
    // };

    const getImageInfo = (name: keyof IForm) => {
      const fileList = values[name] as UploadFile[];
      if (!fileList || !fileList.length) return [];

      const images = fileList.map(file => {
        if (file.url) {
          // Existing image
          return (
            {
              ...file,
              imageUrl: file.url,
              ratio: file.ratio || 0,
              type: 1,
              sort: 0,
            }
          );
        } else if (file.originFileObj) {
          // New uploaded image
          const url = URL.createObjectURL(file.originFileObj);
          return ({
            url,
            imageUrl: url,
            ratio: file.ratio || 0,
            type: 1,
            sort: 0,
          });
        }
      })
      return images.filter(Boolean);
    };

    const data = {
      ...values,
      id: id ? Number(id) : undefined,
      title: values.title,
      venue: values.venue,
      startDateStr: values.schedule?.[0]?.format(),
      endDateStr: values.schedule?.[1]?.format(),
      hours: values.hours,
      closeTime: values.close,
      fee: values.fee,
      website: values.website,
      location: values.location,
      access: values.access,
      phone: values.phone,
      postalCode: values.postCode,
      exhibitionImageList: getImageInfo('cover'),
      startDate: values.schedule?.[0]?.toISOString(),
      endDate: values.schedule?.[1]?.toISOString(),
      extData: JSON.stringify(extraContent),
      status: 1,
      bookStatus: 0,
    };

    if (values.venuePhotos?.length) {
      data.venuePhotos = getImageInfo('venuePhotos');
    }

    return data;
  };

  const renderFormTitle = useMemo(() => {
    return id ? '编辑详情' : '创建';
  }, [id]);

  useEffect(() => {
    const fetchDetailData = async () => {
      const resp = await getExhibitionDetail(Number(id));
      if (resp.status !== 200) {
        return;
      }
      const fileList = resp.data.exhibitionImageList.map((item, index) => ({
        uid: index.toString(),
        name: item.imageName,
        url: item.imageUrl,
        ratio: item.ratio,
      }));

      console.log(fileList, "fileList")
      // setFileList(fileList);

      let parsedExt: unknown = [];
      try {
        parsedExt = resp.data.extData ? JSON.parse(resp.data.extData) : [];
      } catch (e) {
        parsedExt = [];
      }
      let extData: ExhibitionContent[] = [];
      if (Array.isArray(parsedExt)) {
        factpackRef.current = null;
        extData = parsedExt as ExhibitionContent[];
      } else if (parsedExt && typeof parsedExt === 'object') {
        // factpack 形式。追加コンテンツは content キーに入れてある(無ければ空)
        factpackRef.current = parsedExt as Record<string, unknown>;
        const c = (parsedExt as Record<string, unknown>).content;
        extData = Array.isArray(c) ? (c as ExhibitionContent[]) : [];
      }
      const extraContent: ExtraContent[] = extData.map((item, index) => ({
        type: item.imageUrl ? 'image' : 'text',
        imageInfo: item.imageUrl
          ? {
            uid: index.toString(),
            name: '',
            url: item.imageUrl,
            ratio: item.ratio,
          }
          : null,
        text: item.deltaContent
          ? new Delta(JSON.parse(item.deltaContent) as Op[])
          : new Delta(),
        editorRef: createRef(),
      }));
      setExtraContent(extraContent);

      setLocationVal({
        address: resp.data.location,
        lat: resp.data.latitude,
        lng: resp.data.longitude,
      });

      const formValues = {
        ...resp.data,
        cover: fileList,
        title: resp.data.title,
        venue: resp.data.venue,
        schedule: [dayjs(resp.data.startDateStr), dayjs(resp.data.endDateStr)],
        hours: resp.data.hours,
        close: resp.data.closeTime,
        fee: resp.data.fee,
        website: resp.data.website,
        location: resp.data.location,
        access: resp.data.access,
        phone: resp.data.phone,
        postCode: resp.data.postalCode,
      };

      if (resp.data.iconType === IconTypeEnum.Custom) {
        const iconUrlfileList = [{
          url: resp.data.iconUrl,
          uid: 0
        }];
        formValues["iconUrl-custom"] = iconUrlfileList;
      } else {
        formValues["iconUrl-default"] = resp.data.iconUrl;
      }

      if (resp.data.venuePhotoList?.length) {
        formValues.venuePhotos = resp.data.venuePhotoList.map(
          (item, index) => ({
            uid: index.toString(),
            name: item.imageName,
            url: item.imageUrl,
            ratio: item.ratio,
          })
        );
      }

      let location = [];
      if (onlyShowRoad) {
        location = [resp.data.road].filter(Boolean);
      } else {
        location = [
          resp.data.road,
          resp.data.hall,
          resp.data.city,
          resp.data.village,
        ].filter(Boolean);
      }
      formValues.location = location;

      form.setFieldsValue(formValues);

      // Set initial preview data
      setPreviewData({
        ...formValues,
        id: Number(id),
        title: resp.data.title,
        description: resp.data.description,
        venue: resp.data.venue,
        startDateStr: resp.data.startDateStr,
        endDateStr: resp.data.endDateStr,
        hours: resp.data.hours,
        closeTime: resp.data.closeTime,
        fee: resp.data.fee,
        website: resp.data.website,
        location: resp.data.location,
        access: resp.data.access,
        phone: resp.data.phone,
        postalCode: resp.data.postalCode,
        exhibitionImageList: resp.data.exhibitionImageList,
        startDate: resp.data.startDate,
        endDate: resp.data.endDate,
        extData: resp.data.extData,
        status: resp.data.status,
        bookStatus: resp.data.bookStatus,
      });
    };

    if (id) {
      void fetchDetailData();
    }
  }, [id, form]);

  const handleUploadLocalImg = async (cover: UploadFile[]) => {
    const file = cover[0].originFileObj as File;
    setUploading(true);
    const uploadResp = await uploadImage(file);
    setUploading(false);
    if (uploadResp.status !== 200) {
      messageApi.error(uploadResp.message);
      return;
    }

    return uploadResp;
  };

  const getImageParams = async (fileList: UploadFile[]) => {

    const res = Promise.all(fileList.map(async (file) => {
      console.log(file, "file")
      if (file.url) {
        return file;
      }

      const uploadResp = await handleUploadLocalImg([file]);
      if (!uploadResp) return {};
      const data = uploadResp.data;
      console.log(data, " data")
      return {
        ratio: data.ratio,
        url: data.imageUrl,
        uid: file.uid,
        name: file.name
      };
    })).then(res => {
      console.log(res, "res11")
      return res;

    })

    return res;
    // console.log(res, "res")
  };

  const formatLocationBeforeSubmit = (location: string[]) => {
    if (location.length === 1) {
      return {
        location: locationVal?.address,
        latitude: locationVal.lat!,
        longitude: locationVal.lng!,
        road: location[0],
      };
    }

    const locationInfo = (locationFeatures.find(
      (item) => item.N03_004 === location.at(-1) && item.N03_001 === location[0]
    ) || {}) as {
      N03_001?: string;
      N03_002?: string;
      N03_003?: string;
      N03_004?: string;
      N03_007?: string;
    };

    return {
      location: locationVal?.address,
      latitude: locationVal.lat!,
      longitude: locationVal.lng!,
      road: locationInfo.N03_001,
      hall: locationInfo.N03_002,
      city: locationInfo.N03_003,
      village: locationInfo.N03_004,
    };
  };

  const handleCreate = async (draft) => {
    let values;
    if (draft) {
      values = await form.validateFields()
    } else {
      values = await form.getFieldsValue();
    }

    setLoading(true);
    const extraContent = await serializeExtraContent();

    const coverInfo = await getImageParams(values.cover);

    let params = {
      ...values,
      startDateStr: values.schedule[0].format('YYYY-MM-DD'),
      endDateStr: values.schedule[1].format('YYYY-MM-DD'),
      closeTime: values.close,
      postalCode: values.postCode,
      cover: coverInfo,
      extData: mergeExtData(extraContent),
    };

    if (values.iconType === IconTypeEnum.Custom) {
      const iconUrlInfo = await getImageParams(values["iconUrl-custom"]);
      params.iconUrl = iconUrlInfo[0].url;
    } else {
      params.iconUrl = values["iconUrl-default"];
    }

    if (values.venuePhotos?.length) {
      const venuePhotosInfo = await getImageParams(values.venuePhotos);
      params.venuePhotos = venuePhotosInfo;
    }

    if (!locationVal?.address) {
      return message.error("请选择地址");
    }

    const locationInfo = formatLocationBeforeSubmit(params.location);
    params = {
      ...params,
      ...locationInfo,
    };

    if (draft) {
      params.status = ExhibitionStatus.Draft;
    } else {
      params.status = ExhibitionStatus.Approved;
    }

    const resp = await createExhibition(params);
    setUploading(false);
    setLoading(false);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success('创建成功');

    setTimeout(() => {
      const searchParams = new URLSearchParams();
      searchParams.set('tab', 'all');
      navigate(`/exhibition?${searchParams.toString()}`);
    }, 3000);
  };

  const handleUpdate = async (draft) => {

    let values;
    if (draft) {
      values = await form.validateFields()
    } else {
      values = await form.getFieldsValue();
    }

    setLoading(true);
    const extraContent = await serializeExtraContent();

    let imageUrl = '';
    let ratio = 0;
    const coverInfo = await getImageParams(values.cover);
    console.log(coverInfo, "coverInfo")

    let params: ExhibitionUpdateData = {
      ...values,
      id: Number(id),
      // imageUrl,
      // ratio,
      cover: coverInfo,
      startDateStr: values.schedule[0].format('YYYY-MM-DD'),
      endDateStr: values.schedule[1].format('YYYY-MM-DD'),
      closeTime: values.close,
      postalCode: values.postCode,
      extData: mergeExtData(extraContent),
    };

    if (values.iconType === IconTypeEnum.Custom) {
      const iconUrlInfo = await getImageParams(values["iconUrl-custom"]);
      params.iconUrl = iconUrlInfo[0].url;
    } else {
      params.iconUrl = values["iconUrl-default"];
    }

    if (values.venuePhotos?.length) {
      const venuePhotosInfo = await getImageParams(values.venuePhotos);
      params.venuePhotos = venuePhotosInfo;
    }

    if (!locationVal?.address) {
      return message.error("请选择地址");
    }

    const locationInfo = formatLocationBeforeSubmit(params.location);
    params = {
      ...params,
      ...locationInfo,
    };

    if (draft) {
      params.status = ExhibitionStatus.Draft;
    } else {
      params.status = ExhibitionStatus.Approved;
    }

    console.log(params, "params")

    const resp = await updateExhibition(params);

    setLoading(false);
    if (resp.status !== 200) {
      messageApi.error(resp.message);
      return;
    }
    messageApi.success('更新成功');
  };

  const handleSubmit = async (draft = false) => {
    if (id) {
      await void handleUpdate(draft);
    } else {
      await void handleCreate(draft);
    }
  };

  const handleUploadChange = (info: UploadChangeParam, name: keyof IForm) => {
    const file = info.file;
    if (file.size && file.size > 1024 * 1024 * 10) {
      messageApi.error('文件大小不能超过 10MB');
      return;
    }
    form.setFieldValue(name, info.fileList);
    setFileList(info.fileList as ExhibitionImageInfo[]);

    // 更新预览数据
    const values = form.getFieldsValue();
    setPreviewData(transformFormToPreview(values));
  };

  const handleExtraImageChange = (info: UploadChangeParam, index: number) => {
    const file = info.file;
    if (file.size && file.size > 1024 * 1024 * 10) {
      messageApi.error('文件大小不能超过 10MB');
      return;
    }
    setExtraContent((prev) => {
      const newContent = [...prev];
      newContent[index].imageInfo = info.fileList[0] as ExhibitionImageInfo;
      return newContent;
    });
  };

  const handleAddExtraContent = (type: 'image' | 'text') => {
    let imageLength = 0;
    let textLength = 0;
    extraContent.forEach((item) => {
      if (item.type === 'image') {
        imageLength++;
      } else {
        textLength++;
      }
    });
    if (type === 'image' && imageLength >= 20) {
      messageApi.error('最多只能添加20张图片');
      return;
    }
    if (type === 'text' && textLength >= 20) {
      messageApi.error('最多只能添加20个文本字段');
      return;
    }
    setExtraContent((prev) => [
      ...prev,
      { type, imageInfo: null, text: new Delta(), editorRef: createRef() },
    ]);
  };

  const handleRemoveExtraContent = (index: number) => {
    const item = extraContent[index];
    let isEmpty = false;
    if (item.type === 'image') {
      isEmpty = !item.imageInfo;
    } else {
      const delta = item.editorRef.current?.getDelta();
      isEmpty = delta?.length === 1 && delta[0].insert === '\n';
    }
    if (!isEmpty) {
      modal.confirm({
        title: '删除确认',
        content: '删除后, 此处填写的内容将丢失',
        onOk: () => {
          setExtraContent((prev) => prev.filter((_, i) => i !== index));
        },
      });
      return;
    }
    setExtraContent((prev) => prev.filter((_, i) => i !== index));
  };

  const serializeExtraContent = async () => {
    const result: ExhibitionContent[] = [];

    const semaphore = new Semaphore(5);
    for (let i = 0; i < extraContent.length; i++) {
      let content: ExhibitionContent = {
        imageUrl: '',
        ratio: 0,
        deltaContent: '',
        htmlContent: '',
      };
      const item = extraContent[i];
      if (item.type === 'image' && item.imageInfo) {
        if (item.imageInfo.url) {
          // 存在历史图片, 直接使用
          content.imageUrl = item.imageInfo.url;
          content.ratio = item.imageInfo.ratio || 0;
        } else {
          // 不存在历史图片, 上传新图片
          setUploading(true);
          await semaphore.acquire();
          const resp = await uploadImage(item.imageInfo.originFileObj as File);
          semaphore.release();
          setUploading(false);
          if (resp.status !== 200) {
            messageApi.error(resp.message);
            return;
          }
          content.imageUrl = resp.data.imageUrl;
          content.ratio = resp.data.ratio;
        }
      } else {
        const html = item.editorRef.current?.getHtml() || '';
        const delta = item.editorRef.current?.getDelta() || [];
        content.htmlContent = html;
        content.deltaContent = JSON.stringify(delta);
      }
      // 过滤空数据
      if (content.imageUrl || content.htmlContent) {
        result.push(content);
      }
    }
    semaphore.clear();
    return result;
  };

  const [visbile, setVisible] = useState<boolean>(false);
  const [locationVal, setLocationVal] = useState<ILocation>({});

  function openMap() {
    setVisible(true);
  }

  const location = Form.useWatch('location', form);

  const onLocationSelect = (value: ILocation) => {
    // console.log(value, 'value');
    // setLocationVal(value.address)
    setLocationVal(value);
    setVisible(false);
  };

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <Flex align="center" gap={10}>
        <Button onClick={() => navigate(-1)} icon={<LeftOutlined />} />
        <h3>{renderFormTitle}</h3>
      </Flex>

      <Row gutter={24}>
        <Col span={16}>
          <Form
            form={form}
            style={{ margin: '20px auto 0' }}
            onFinish={() => handleSubmit(false)}
            onValuesChange={(_, values) => {
              setPreviewData(transformFormToPreview({ ...values }));
            }}
            labelAlign="right"
            labelCol={{
              span: 4,
            }}
            labelWrap={true}
          >
            <Form.Item
              label="cover"
              name="cover"
              rules={[{ required: true, message: '请上传封面' }]}
            >
              <UploadImage maxCount={10} />
              {/* <Upload
                listType="picture"
                accept="image/*"
                beforeUpload={() => false}
                fileList={fileList}
                onChange={(info: UploadChangeParam) =>
                  handleUploadChange(info, 'cover')
                }
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>上传封面</Button>
              </Upload> */}
            </Form.Item>
            <Form.Item
              label="title"
              name="title"
              rules={[{ required: true, message: '请输入标题' }]}
            >
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item label="description" name="description">
              <Input.TextArea rows={4} maxLength={5000} />
            </Form.Item>
            <Form.Item
              label="venue"
              name="venue"
              rules={[{ required: true, message: '请输入场馆' }]}
            >
              <Input maxLength={500} />
            </Form.Item>
            <Form.Item
              label="schedule"
              name="schedule"
              rules={[{ required: true, message: '请输入展览时间' }]}
            >
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="type"
              label="events type"
              rules={[{ required: true }]}
            >
              <Select>
                {[...TypeMap.keys()].map((type) => {
                  const label = TypeMap.get(type);
                  return (
                    <Select.Option key={label} value={type}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              name="iconType"
              label="events icon"
              // rules={[{ required: true }]}
            >
              <Select>
                {[...IconTypeEnumMap.keys()].map((type) => {
                  const label = IconTypeEnumMap.get(type);
                  return (
                    <Select.Option key={label} value={type}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.iconType !== currentValues.iconType}
            >
              {({ getFieldValue }) =>
                getFieldValue('iconType') === IconTypeEnum.Custom ? (
                  <Form.Item
                    name="iconUrl-custom"
                    label={null}
                    // rules={[{ required: true }]}
                  >
                    <UploadImage maxCount={10} />
                  </Form.Item>
                ) : getFieldValue('iconType') === IconTypeEnum.Default ? (
                  <Form.Item
                    name="iconUrl-default"
                    label={null}
                    // rules={[{ required: true }]}
                  >
                    <Radio.Group
                      options={
                        iconUrlDefaultArr.map(item => ({
                          value: item,
                          className: "iconUrl-default-item",
                          label: (
                            <img src={item} />
                          ),
                        }))
                      }
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item label="Comment Rewards" required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="rewardType"
                  rules={[{ required: true }]}
                  style={{ width: "60%" }}
                >
                  <Select>
                    {[...RewardTypeEnumMap.keys()].map((type) => {
                      const label = RewardTypeEnumMap.get(type);
                      return (
                        <Select.Option key={label} value={type}>
                          {label}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="rewardNumber"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              name="recommend"
              label="Official recommendation"
              rules={[{ max: 20, type: 'string' }]}
            >
              <Input count={{ max: 20, show: true }} />
            </Form.Item>
            <Form.Item
              label="working time"
              name="hours"
              rules={[{ required: true, message: '请输入开放时间' }]}
            >
              <Input.TextArea maxLength={500} rows={4} />
            </Form.Item>
            <Form.Item
              label="resting time"
              name="close"
              rules={[{ required: true, message: '请输入关闭时间' }]}
            >
              <Input.TextArea maxLength={500} rows={4} />
            </Form.Item>
            <Form.Item
              label="fee"
              name="fee"
              rules={[{ max: 100, type: 'string' }]}
            >
              <Input.TextArea rows={4} count={{ max: 100, show: true }} />
            </Form.Item>
            <Form.Item
              label="website"
              name="website"
              rules={[{ type: 'url', message: '请输入正确的网址' }]}
            >
              <Input maxLength={500} />
            </Form.Item>
            <Form.Item
              label="post code"
              name="postCode"
              rules={[{ required: true, message: '请输入邮编' }]}
            >
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item label="Venue photos" name="venuePhotos">
              <UploadImage />
            </Form.Item>
            <Form.Item
              label="location"
              name="location"
              rules={[{ required: true, message: '请输入地址' }]}
            >
              {/* <Input.TextArea maxLength={500} rows={4} /> */}
              <Cascader options={locationOptions} />
            </Form.Item>
            {/* <Form.Item name="detail">
              <Input readOnly onClick={openMap} />
            </Form.Item> */}
            <Row>
              <Col span={4}></Col>
              <Col>
                <Button onClick={openMap}>
                  <img src={locationIcon} />
                  {locationVal?.address ?? 'Click to select detailed address'}
                </Button>
              </Col>
            </Row>

            <Form.Item label="access" name="access">
              <Input.TextArea maxLength={500} rows={4} />
            </Form.Item>
            <Form.Item
              label="phone"
              name="phone"
              rules={[
                {
                  pattern:
                    /^(\+?\d{1,4})?[-.\s]?\(?\d{1,3}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
                  message: '请输入正确的电话号码格式',
                },
              ]}
            >
              <Input maxLength={20} />
            </Form.Item>

            <Divider orientation="left">扩展内容</Divider>
            {extraContent.map((item, index) => (
              <Form.Item key={index}>
                <div style={{ border: '1px solid #ccc', padding: 10 }}>
                  {item.type === 'image' && (
                    <Upload
                      listType="picture"
                      accept="image/*"
                      beforeUpload={() => false}
                      fileList={item.imageInfo ? [item.imageInfo] : []}
                      onChange={(info) => handleExtraImageChange(info, index)}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>上传图片</Button>
                    </Upload>
                  )}
                  {item.type === 'text' && (
                    <div>
                      <RichEditor
                        ref={item.editorRef}
                        defaultValue={item.text}
                        maxLength={5000}
                      />
                      <div style={{ color: '#ccc', fontSize: 12 }}>
                        最大5000字符
                      </div>
                    </div>
                  )}
                  <Flex style={{ marginTop: 10 }} justify="end">
                    <Button
                      onClick={() => handleRemoveExtraContent(index)}
                      icon={<DeleteOutlined />}
                    />
                  </Flex>
                </div>
              </Form.Item>
            ))}

            <Form.Item>
              <Space size="small">
                <Button onClick={() => handleAddExtraContent('image')}>
                  新增图片字段
                </Button>
                <Button onClick={() => handleAddExtraContent('text')}>
                  新增文本字段
                </Button>
              </Space>
            </Form.Item>

            <Divider />
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                提交
              </Button>
              <Button onClick={() => handleSubmit(true)} loading={uploading}>
                存草稿
              </Button>
            </Space>
          </Form>
        </Col>
        <Col span={8}>
          <div style={{ position: 'sticky', top: 20, marginTop: 20 }}>
            <EventRenderer
              type="exhibition"
              event={previewData}
              mode="preview"
            />
          </div>
        </Col>
      </Row>
      {modalContextHolder}
      <Modal open={visbile} onCancel={() => setVisible(false)} footer={null}>
        {/* <div id="map" style={{ height: 500 }}></div> */}
        <div style={{ marginTop: 30 }}>
          <Places
            location={locationVal?.address ?? location}
            lat={locationVal?.lat}
            lng={locationVal?.lng}
            onSelect={onLocationSelect}
          />
        </div>

      </Modal>
    </Spin>
  );
};

export default ExhibitionEditor;
